"use client";

// biome-ignore lint/nursery/noDeprecatedImports: upstream Contentful hook is still required for live preview updates
import { useContentfulLiveUpdates } from "@scott/contentful/live-preview-init";
import type { ReactNode } from "react";
import { ContentBlocks } from "@/components/blocks/content-blocks/content-blocks";
import { useServerBlockRefresh } from "@/hooks/use-server-block-refresh";
import type { HomePage } from "@/services/contentful/assemblies/home-page/fetch-home-page";
import { contentBlocksAdapter } from "@/services/contentful/assemblies/page/adapter/content-blocks.adapter";
import { withSectionDocuments } from "@/services/contentful/assemblies/sections/section-documents";
import { HomePageDocument } from "@/services/contentful/generated/contentful-sdk.generated";
import type { PageContext } from "@/types/page-context";
import { ContentPageSurface } from "../content-page/content-page-surface";

const LIVE_PREVIEW_QUERY = withSectionDocuments(HomePageDocument);

type HomePageLivePreviewProps = {
  /** The active market's `/{country}/{lang}`, for internal page links. */
  basePath: string;
  locale: string;
  page: HomePage;
  pageContext: PageContext["pageContext"];
  serverSlots: Map<string, ReactNode>;
};

export function HomePageLivePreview(props: Readonly<HomePageLivePreviewProps>) {
  const updatedPage = useContentfulLiveUpdates(props.page, {
    locale: props.locale,
    query: LIVE_PREVIEW_QUERY,
  });

  useServerBlockRefresh(
    updatedPage,
    (page) => [
      ...(page.introSectionCollection?.items ?? []),
      ...(page.contentSectionsCollection?.items ?? []),
      ...(page.bottomSectionCollection?.items ?? []),
    ],
    props.serverSlots
  );

  const introBlocks = contentBlocksAdapter(
    [...(updatedPage.introSectionCollection?.items ?? [])],
    props.locale,
    props.basePath
  );
  const contentBlocks = contentBlocksAdapter(
    [...(updatedPage.contentSectionsCollection?.items ?? [])],
    props.locale,
    props.basePath
  );
  const bottomBlocks = contentBlocksAdapter(
    [...(updatedPage.bottomSectionCollection?.items ?? [])],
    props.locale,
    props.basePath
  );

  return (
    <ContentPageSurface
      firstBlock={introBlocks[0] ?? contentBlocks[0] ?? bottomBlocks[0]}
    >
      <ContentBlocks
        blocks={introBlocks}
        pageContext={props.pageContext}
        serverSlots={props.serverSlots}
      />
      <ContentBlocks
        blocks={contentBlocks}
        pageContext={props.pageContext}
        serverSlots={props.serverSlots}
      />
      <ContentBlocks
        blocks={bottomBlocks}
        pageContext={props.pageContext}
        serverSlots={props.serverSlots}
      />
    </ContentPageSurface>
  );
}
